import axios from 'axios'
import { getToken } from '@BUP/services/auth.js'
import { monitorException, setMonitoredContext } from '../monitor'
import { reportErrorOnce } from '../monitor/reportErrorOnce'
const BRAVEUP_API_HOST = import.meta.env.VITE_SCHOOLS_API_HOST
const SOCIOGRAM_API_URL = import.meta.env.VITE_SOCIOGRAM_URL
const BRAVEUP_API_HOST_V2 = import.meta.env.VITE_SCHOOLS_API_HOST_V2
const BRAVEUP_HOST = import.meta.env.VITE_SCHOOLS_API_HOST_ROOT

function getBaseURL (name, version) {
  switch (name) {
    case 'schools':
      switch (version) {
        case 1:
          return BRAVEUP_API_HOST
        case 2:
          return BRAVEUP_API_HOST_V2
        case 3:
          return `${BRAVEUP_HOST}/api/v3`
        case 4:
          return `${BRAVEUP_HOST}/api/v4`
        case 5:
          return `${BRAVEUP_HOST}/api/v5`
      }
      break
    case 'sociogram':
      return SOCIOGRAM_API_URL
    case 'root':
      return BRAVEUP_HOST
  }
}

const STATUS_CODE_TO_ERROR = {
  400: 'api/bad-request',
  401: 'api/no-authorized',
  403: 'api/forbidden',
  404: 'api/not-found',
  405: 'api/not-allowed',
  409: 'api/conflict',
  422: 'api/unprocessable-entity',
  500: 'api/server-error',
  502: 'api/bad-gateway',
  503: 'api/service-unavailable',
  504: 'api/gateway-timeout'
}

const parsePayload = (data) => {
  if (!data) return data

  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    return '[form-data]'
  }

  if (typeof URLSearchParams !== 'undefined' && data instanceof URLSearchParams) {
    return data.toString()
  }

  if (typeof data === 'string') {
    try {
      return JSON.parse(data)
    } catch {
      return data
    }
  }

  return data
}

const buildApiContext = (error, meta = {}) => {
  const config = error?.config ?? {}
  const response = error?.response ?? {}
  const requestInfo = {
    ...meta,
    code: error?.code,
    message: error?.message,
    method: config?.method?.toUpperCase(),
    url: config?.url,
    baseURL: config?.baseURL,
    serviceParams: config?.params,
    timeout: config?.timeout,
    requestHeaders: config?.headers,
    payload: parsePayload(config?.data),
    status: response?.status,
    statusText: response?.statusText,
    responseHeaders: response?.headers,
    responseData: response?.data
  }

  if (config?.metadata) {
    requestInfo.requestMetadata = config.metadata
  }

  return requestInfo
}

const annotateErrorCode = (error) => {
  if (error.response) {
    error.code = STATUS_CODE_TO_ERROR[error.response.status] ?? 'api/unknown-error'
    return
  }

  if (error.request) {
    error.code = error.code ?? 'api/no-response'
    return
  }

  error.code = error.code ?? 'api/request-config'
}

const attachAxiosInterceptor = (instance, meta) => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      annotateErrorCode(error)
      reportErrorOnce(error, () => {
        setMonitoredContext('api', buildApiContext(error, meta))
        monitorException(error)
      })

      return Promise.reject(error)
    }
  )
}

export function getApi (name, options = { version: 2, authenticated: true }) {
  options = { version: 2, authenticated: true, ...options }
  const baseURL = getBaseURL(name, options.version)
  const instance = axios.create({ baseURL, timeout: 30000 })

  instance.defaults.metadata = {
    service: name,
    version: options.version
  }

  attachAxiosInterceptor(instance, instance.defaults.metadata)

  if (!options.authenticated) return instance

  return getToken().then(token => {
    instance.defaults.headers.common.authorization = `Bearer ${token}`
    return instance
  })
}

export async function postFetcher (url, { arg }) {
  const client = await getApi('schools', { authenticated: true, version: 4 })
  return client
    .post(url, arg)
    .then(response => response.data)
}

export async function patchFetcher (url, { arg }) {
  const client = await getApi('schools', { authenticated: true, version: 4 })
  return client
    .patch(url, arg)
    .then(response => response.data)
}

export async function getFetcher (url) {
  const client = await getApi('schools', { authenticated: true, version: 4 })
  return client
    .get(url)
    .then(response => response.data)
}

export async function postFileFetcher (url, { arg }) {
  const client = await getApi('schools', { authenticated: true, version: 4 })
  client.defaults.headers.common['Content-Type'] = 'multipart/form-data'
  return client
    .post(url, arg)
    .then(response => response.data)
}

export async function getSociogramFetcher (url) {
  const client = await getApi('sociogram', { authenticated: true })
  return client.get(url)
    .then(response => response.data)
    .catch(error => {
      if (error.response && error.response.status === 204) {
        return null
      }
      throw error
    })
}
